import { SaveOutlined } from "@ant-design/icons";
import type { SupportTicket } from "@features/support/api";
import {
  useMarkSupportRead,
  useSendSupportMessage,
  useSupportMessages,
  useUpdateSupportTicket,
} from "@features/support/api";
import { type AdminUser, useAdminUsers } from "@features/users/api";
import { extractErrorMessage } from "@shared/extractErrorMessage";
import { resolveDrawerSize } from "@shared/ui/enterprise-drawer/drawerSize";
import { App as AntdApp, Button, Drawer, Flex, Input, Select, Tooltip } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import SupportMessageInput from "./SupportMessageInput";
import SupportMessageThread from "./SupportMessageThread";

interface Props {
  ticket?: SupportTicket;
  open: boolean;
  onClose: () => void;
  isAdmin: boolean;
}

const statusLabels: Record<string, string> = {
  new: "Новый",
  accepted: "В работе",
  blocked: "Заблокирован",
  closed: "Закрыт",
};

const statusTransitions: Record<string, string[]> = {
  new: ["accepted", "closed"],
  accepted: ["closed", "blocked"],
  blocked: ["accepted", "closed"],
  closed: [],
};

const formatUserName = (user: AdminUser) => {
  const parts = [user.last_name, user.first_name, user.middle_name].filter(Boolean);
  return parts.join(" ") || user.id;
};

const SupportDetailDrawer = ({ ticket, open, onClose, isAdmin }: Props) => {
  const { message } = AntdApp.useApp();
  const { data: messages } = useSupportMessages(ticket?.id);
  const usersQuery = useAdminUsers();
  const sendMessage = useSendSupportMessage();
  const updateTicket = useUpdateSupportTicket();
  const { mutate: markAsRead } = useMarkSupportRead();
  const markedRef = useRef<string | undefined>(undefined);
  const [draftStatus, setDraftStatus] = useState("new");
  const [draftTitle, setDraftTitle] = useState("");
  const [draftAssignee, setDraftAssignee] = useState("");

  useEffect(() => {
    if (!open || !ticket?.id) return;
    if (markedRef.current === ticket.id) return;
    markedRef.current = ticket.id;
    markAsRead({ id: ticket.id });
  }, [open, ticket?.id, markAsRead]);

  useEffect(() => {
    if (!open) markedRef.current = undefined;
  }, [open]);

  useEffect(() => {
    setDraftStatus(ticket?.status ?? "new");
    setDraftTitle(ticket?.title ?? "");
    setDraftAssignee(ticket?.assigned_to ?? "");
  }, [ticket?.status, ticket?.title, ticket?.assigned_to]);

  const statusOptions = useMemo(() => {
    const current = ticket?.status ?? "new";
    const allowed = new Set([current, ...(statusTransitions[current] ?? [])]);
    return Object.keys(statusLabels).map((value) => ({
      value,
      label: statusLabels[value] ?? value,
      disabled: !allowed.has(value),
    }));
  }, [ticket?.status]);

  const assigneeOptions = useMemo(() => {
    const admins = (usersQuery.data ?? []).filter((u) => u.role === "admin");
    return [
      { value: "", label: "Не назначен" },
      ...admins.map((u) => ({ value: u.id, label: formatUserName(u) })),
    ];
  }, [usersQuery.data]);

  const isDirty = useMemo(() => {
    if (!ticket) return false;
    return (
      draftStatus !== (ticket.status ?? "new") ||
      draftTitle.trim() !== (ticket.title ?? "") ||
      draftAssignee !== (ticket.assigned_to ?? "")
    );
  }, [ticket, draftStatus, draftTitle, draftAssignee]);

  const handleSave = () => {
    if (!ticket) return;
    const payload: { status?: string; title?: string; assigned_to?: string } = {};
    if (draftStatus !== (ticket.status ?? "new")) payload.status = draftStatus;
    if (draftTitle.trim() !== (ticket.title ?? "")) payload.title = draftTitle.trim();
    if (draftAssignee !== (ticket.assigned_to ?? "")) payload.assigned_to = draftAssignee;
    if (Object.keys(payload).length === 0) return;

    updateTicket.mutate(
      { id: ticket.id, data: payload as never },
      {
        onSuccess: () => message.success("Обновлено"),
        onError: (err: unknown) => {
          message.error(extractErrorMessage(err));
          setDraftStatus(ticket.status ?? "new");
          setDraftTitle(ticket.title ?? "");
          setDraftAssignee(ticket.assigned_to ?? "");
        },
      },
    );
  };

  const handleSend = (text: string) => {
    if (!ticket) return;
    sendMessage.mutate(
      { id: ticket.id, data: { body: text } },
      { onError: (err: unknown) => message.error(extractErrorMessage(err)) },
    );
  };

  return (
    <Drawer
      title="Обращение"
      open={open}
      onClose={onClose}
      size={resolveDrawerSize("small")}
      extra={
        isAdmin && ticket ? (
          <Flex align="center" gap={8}>
            <Select
              value={draftStatus}
              onChange={setDraftStatus}
              options={statusOptions}
              style={{ minWidth: 170 }}
              size="middle"
            />
            <Tooltip title={isDirty ? "Сохранить изменения" : "Нет изменений"}>
              <Button
                type="primary"
                icon={<SaveOutlined />}
                aria-label="Сохранить изменения"
                onClick={handleSave}
                disabled={!isDirty}
                loading={updateTicket.isPending}
              />
            </Tooltip>
          </Flex>
        ) : null
      }
      styles={{ body: { display: "flex", flexDirection: "column", padding: "8px 16px" } }}
    >
      {isAdmin && ticket ? (
        <Flex
          align="center"
          gap={8}
          style={{ padding: "4px 0 8px", borderBottom: "1px solid var(--ant-color-border)" }}
        >
          <Input
            value={draftTitle}
            onChange={(e) => setDraftTitle(e.target.value)}
            placeholder="Название обращения"
            size="middle"
            style={{ flex: 1 }}
          />
          <Select
            value={draftAssignee}
            onChange={setDraftAssignee}
            options={assigneeOptions}
            style={{ minWidth: 180 }}
            loading={usersQuery.isLoading}
            size="middle"
          />
        </Flex>
      ) : null}
      <Flex vertical flex={1} style={{ overflow: "auto" }}>
        <SupportMessageThread messages={messages ?? []} ticketId={ticket?.id} />
      </Flex>
      {ticket?.status !== "closed" && (
        <div style={{ paddingTop: 8 }}>
          <SupportMessageInput onSend={handleSend} loading={sendMessage.isPending} />
        </div>
      )}
    </Drawer>
  );
};

export default SupportDetailDrawer;
